import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { differenceInYears } from 'date-fns';
import useConsultantBookingsForm from '../hooks/useConsultantBookingForm';
import FormField from '../components/FormField';
import CTAButton from '../components/CTAButton';
import InitialBookingDisclaimer from '../components/InitialBookingDisclaimer';
import CommunicationCard from '../components/CommunicationCard';
import { ReactComponent as Checkmark } from '../static/img/checkmark.svg';
import ConsultantBookingError from '../components/ConsultantBookingError';

import './ConsultantForm.sass';
import consultantBookingErrorActions from '../actions/consultantBookingErrorActions';

export default () => {
  const { state } = useLocation();
  const history = useHistory();
  const { logConsultantBookingError: logError } = consultantBookingErrorActions();

  const { detailsForm, toggleTerms, action, formComplete } = useConsultantBookingsForm({
    ...state,
    initialValues: state.formValues || {},
  });

  const [appointmentType, setAppointmentType] = useState('initial');
  const [patientUnder16, setPatientUnder16] = useState(false);
  const [patient16or17, setPatient16or17] = useState(false);
  const [patientUnder18, setPatientUnder18] = useState(false);
  const [dobFutureDate, setDobFutureDate] = useState(false);
  const [showAge, setShowAge] = useState(false);
  const [patientAge, setPatientAge] = useState();
  const [patientMakingTheirOwnBooking, setPatientMakingTheirOwnBooking] = useState(
    state.formValues?.patientMakingTheirOwnBooking ?? true,
  );

  const errorState = useSelector(s => s.consultantBookingError);
  const showConsultantError = Boolean(
    errorState && (errorState.error?.errorCode || errorState.text || errorState.error),
  );

  const [bypassPayment, setBypassPayment] = useState(false);

  const typeCHospitals = [
    'Nuffield Health Derby Hospital',
    'Nuffield Health Haywards Heath Hospital',
    'Nuffield Health Ipswich Hospital',
    'Nuffield Health Tees Hospital',
  ];
  const typeDHospitals = [
    'Nuffield Health Chichester Hospital',
    'Nuffield Health Hereford (Wye Valley) Hospital',
    'Nuffield Health Highgate Hospital',
    'Nuffield Health Barts Hospital',
    'Nuffield Health Shrewsbury Hospital',
    'Nuffield Health Taunton Hospital',
    'Nuffield Health Wessex Hospital',
    'Nuffield Health Woking Hospital',
  ];
  function checkHospital(hospitalList, hospitalName) {
    return hospitalList.includes(hospitalName);
  }
  const typeCHospitalTrue = checkHospital(typeCHospitals, state.hospital.hospitalName);
  const typeDHospitalTrue = checkHospital(typeDHospitals, state.hospital.hospitalName);

  const day = document.querySelector('#date_of_birth.m--day')?.value;
  const month = document.querySelector('#date_of_birth.m--month')?.value;
  const year = document.querySelector('#date_of_birth.m--year')?.value;
  const dobDate = year && month && day ? new Date(year, month - 1, day) : null;

  const scrollParent = () => {
    window.parent.postMessage({ type: 'SCROLL_TO_BOOKING' }, state.parentURL);
  };

  useEffect(() => {
    const patientAgeCalc = dobDate
      ? differenceInYears(new Date(state.slotDate), new Date(dobDate))
      : NaN;
    const todayDate = new Date();
    const dobField = document.querySelector('.date-field__outer-wrapper')?.parentElement;
    const errorField = dobField && dobField.querySelector('.field-error');
    const CTAButton = document.querySelector('.primary-btn.m--consultant');
    let patientUnder16 = false,
      patient16or17 = false,
      patientUnder18 = false;

    if (typeDHospitalTrue && patientAgeCalc < 18) {
      patientUnder18 = true;
      dobField && dobField.classList.add('field--invalid');
      if (CTAButton) CTAButton.disabled = true;
    } else if (typeCHospitalTrue && patientAgeCalc < 18 && patientAgeCalc >= 16) {
      patient16or17 = true;
      dobField && dobField.classList.add('field--invalid');
      if (CTAButton) CTAButton.disabled = true;
    } else if (typeCHospitalTrue && patientAgeCalc < 16) {
      patientUnder16 = true;
      dobField && dobField.classList.add('field--invalid');
      if (CTAButton) CTAButton.disabled = true;
    } else if (!errorField) {
      setPatientAge(Math.floor(patientAgeCalc));
      dobField && dobField.classList.remove('field--invalid');
    }

    if (
      dobField &&
      dobField.classList.contains('field--invalid') &&
      errorField &&
      dobDate > todayDate
    ) {
      setDobFutureDate(true);
      errorField.innerHTML =
        'The date of birth entered cannot be in the future. Please enter a valid date of birth.';
    } else if (
      dobField &&
      dobField.classList.contains('field--invalid') &&
      errorField &&
      dobDate < todayDate
    ) {
      setDobFutureDate(false);
      errorField.innerHTML = 'Please enter a valid date of birth.';
    }

    setPatientUnder16(patientUnder16);
    setPatient16or17(patient16or17);
    setPatientUnder18(patientUnder18);

    if (
      dobField &&
      !dobField.classList.contains('field--invalid') &&
      !isNaN(dobDate) &&
      dobDate !== null &&
      !isNaN(patientAge)
    ) {
      setShowAge(true);
    } else {
      setShowAge(false);
    }
  }, [dobDate, state.slotDate, typeCHospitalTrue, typeDHospitalTrue, patientAge]);

  function whoIsThePatient(e) {
    setPatientMakingTheirOwnBooking(e.target.value === 'myself');
  }

  useEffect(() => {
    const bypass = sessionStorage.getItem('bypassPayment');
    bypass === 'true' ? setBypassPayment(true) : setBypassPayment(false);
  }, []);

  const mandatoryPaymentHospitals = process.env.REACT_APP_MANDATORY_PAYMENT.split(',');
  const optionalPaymentHospitals = process.env.REACT_APP_OPTIONAL_PAYMENT.split(',');
  const mandatoryPayment = mandatoryPaymentHospitals.includes(state.hospital.hospitalId);
  const optionalPayment = optionalPaymentHospitals.includes(state.hospital.hospitalId);

  return (
    <>
      <InitialBookingDisclaimer />
      <div className="m--consultant-form">
        <div className="pattern" />
        <div className="header__wrapper mb-40">
          <div className="header">
            <h2 className="h2">Book consultant</h2>
          </div>
        </div>
        <ConsultantBookingError />
        <fieldset>
          <legend className="page-title">What&apos;s the purpose of your appointment?</legend>
          <div className="appointment-type-section">
            <label
              className={`appointment-option-container ${appointmentType === 'initial' ? 'active-container' : ''}`}
            >
              <input
                type="radio"
                name="appointmentType"
                id="initial"
                className="radio-btn"
                checked={appointmentType === 'initial'}
                onChange={() => setAppointmentType('initial')}
              />
              <div className="icon-wrapper" aria-hidden="true">
                <div
                  className={`icon-container ${appointmentType === 'initial' ? 'active-icon' : ''}`}
                ></div>
              </div>
              <div className="appointment-text-container">
                <p className="select-title">Initial appointment</p>
                <p className="select-text">
                  Choose this if it is your first appointment with this consultant or you wish to
                  discuss a new concern.
                </p>
              </div>
            </label>
            <label
              className={`appointment-option-container ${appointmentType === 'followUp' ? 'active-container' : ''}`}
            >
              <input
                type="radio"
                name="appointmentType"
                id="followUp"
                className="radio-btn"
                checked={appointmentType === 'followUp'}
                onChange={() => setAppointmentType('followUp')}
              />
              <div className="icon-wrapper" aria-hidden="true">
                <div
                  className={`icon-container ${appointmentType === 'followUp' ? 'active-icon' : ''}`}
                ></div>
              </div>
              <div className="appointment-text-container">
                <p className="select-title">Follow-up appointment</p>
                <p className="select-text">
                  Choose this if you have already seen this consultant and have ongoing care.
                </p>
              </div>
            </label>
          </div>
        </fieldset>
        {appointmentType === 'followUp' && (
          <div className="follow-up-section">
            <p className="follow-up-title">How to book a follow-up appointment</p>
            <div className="follow-up-description">
              <p>
                Please call us on <a href="tel:03001231995">0300 123 1995</a> to arrange your
                follow-up appointment.
              </p>
              <p className="opening-hours-title">Opening hours: </p>
              <p>Monday to Friday: 8am to 8pm </p>
              <p>Saturday: 9am to 1pm </p>
              <p>Sunday and Bank Holidays: closed</p>
            </div>

            <a className="primary-btn follow-up-btn" href="tel:03001231995">
              Call: 0300 123 1995
            </a>
          </div>
        )}
        {!showConsultantError && appointmentType === 'initial' && (
          <>
            <form className="form-row">
              <fieldset className="form-radio-block">
                <legend className="page-title">Who is the consultation for?</legend>
                <div className="radio-block-options">
                  <div className="radio-block-option">
                    <input
                      type="radio"
                      id="myself"
                      name="whoIsThePatient"
                      value="myself"
                      onChange={e => {
                        whoIsThePatient(e);
                      }}
                      checked={patientMakingTheirOwnBooking}
                    ></input>
                    <label htmlFor="myself">Myself</label>
                  </div>
                  <div className="radio-block-option">
                    <input
                      type="radio"
                      id="someoneElse"
                      name="whoIsThePatient"
                      value="someoneElse"
                      onChange={e => {
                        whoIsThePatient(e);
                      }}
                      checked={!patientMakingTheirOwnBooking}
                    ></input>
                    <label htmlFor="someoneElse">Someone else</label>
                  </div>
                </div>
              </fieldset>
            </form>

            {typeCHospitalTrue && (
              <div className="mb-40 under18-disclaimer">
                <h3 className="under18-disclaimer-title">Patients under 18</h3>
                <p className="pt-10">
                  <span className="bold">Under 16: </span>This hospital does not see patients under
                  16. Please{' '}
                  <a href="https://www.nuffieldhealth.com/consultants" target="_parent">
                    select a different hospital
                  </a>
                  .
                </p>
                <p className="pt-10">
                  <span className="bold">16 & 17-year-olds: </span>We are unable to take online
                  bookings, please call <a href="tel:03456029262">0345 602 9262</a> to make your
                  appointment.
                </p>
              </div>
            )}
            {typeDHospitalTrue && (
              <div className="mb-40 under18-disclaimer">
                <h3 className="under18-disclaimer-title">Patients under 18</h3>
                <p className="pt-10">
                  This hospital does not take bookings for those below the age of 18. If you are
                  under 18 or are booking on behalf of someone who is under 18, please{' '}
                  <a href="https://www.nuffieldhealth.com/consultants" target="_parent">
                    select a different hospital
                  </a>
                  .
                </p>
              </div>
            )}
            {patientMakingTheirOwnBooking ? (
              <>
                <h3 className="page-title">Your details</h3>
                <div className="mb-30">
                  <p>
                    Please fill out your details. We need this information to confirm your
                    appointment.
                  </p>
                  <p className="pt-10">* Mandatory field</p>
                </div>
              </>
            ) : (
              <>
                <CommunicationCard
                  type="info"
                  copy="Please fill out the form below with the patient's details. We need this information to confirm their appointment."
                />
                <h3 className="page-title">Your details</h3>
                <div className="mb-30">
                  <p className="pt-10">* Mandatory field</p>
                </div>
              </>
            )}
            <div className="fields mb-30 title-field__wrapper">
              <FormField field={detailsForm.title} errorEmpty="Please select your title." />
            </div>
            <div className="fields">
              <FormField
                field={detailsForm.first_name}
                errorText="Please re-enter the name without accented letters or spaces. For example, Verónica should be Veronica."
                errorEmpty="Please enter your first name."
              />
              <FormField
                field={detailsForm.last_name}
                errorText="Please re-enter the name without accented letters or spaces. For example, Pérez - Fernández should be Perez-Fernandez."
                errorEmpty="Please enter your last name."
              />
              <div className="remaining-fields">
                <FormField
                  field={detailsForm.email}
                  errorText="Please use the following format: name@domain.com"
                  errorEmpty="Please enter your email address."
                />
                <FormField
                  field={detailsForm.addresslookup}
                  errorEmpty="Please enter your address."
                />
                <div className="gender-field__wrapper">
                  <FormField field={detailsForm.gender} errorEmpty="Please select your gender." />
                </div>
                <div>
                  <FormField
                    field={detailsForm.date_of_birth}
                    errorText="Please enter a valid date of birth."
                  />
                  {showAge && (
                    <p className="patient_age">Age on date of appointment: {patientAge}</p>
                  )}

                  {typeCHospitalTrue && patientUnder16 && !dobFutureDate && (
                    <CommunicationCard
                      type="error"
                      icon="true"
                      title="Patient is under 16"
                      copy="Sorry we're unable to book patients under the age of 16 at this hospital. Please <a href='https://www.nuffieldhealth.com/consultants' target='_parent'> select a different hospital</a> or call <a href='tel:03456029262'>0345 602 9262</a>."
                    />
                  )}
                  {typeCHospitalTrue && patient16or17 && !dobFutureDate && (
                    <CommunicationCard
                      type="error"
                      icon="true"
                      title="Patient is 16 or 17"
                      copy="We’re unable to take bookings online for patients aged 16 and 17. Please call <a href='tel:03456029262'>0345 602 9262</a> to make your appointment."
                    />
                  )}
                  {typeDHospitalTrue && patientUnder18 && !dobFutureDate && (
                    <CommunicationCard
                      type="error"
                      icon="true"
                      title="Patient is under 18"
                      copy="Sorry, we’re unable to see patients under the age of 18 at this hospital. Please <a href='https://www.nuffieldhealth.com/consultants' target='_parent'> select a different hospital</a> or call <a href='tel:03456029262'>0345 602 9262</a>."
                    />
                  )}
                  {!typeCHospitalTrue && !typeDHospitalTrue && (
                    <p>
                      If you are under 18 or making an appointment on behalf of someone who is,
                      check that your chosen consultant accepts paediatric patients before booking.
                      If you are unsure, please call <a href="tel:03456029262">0345 602 9262</a>.
                    </p>
                  )}
                </div>
                <FormField
                  field={detailsForm.phone_number}
                  errorEmpty="Please enter your phone number."
                  errorText="Enter a number starting with 0, the correct amount of digits, and remove any spaces or invalid characters."
                />
                <div>
                  <FormField
                    field={detailsForm.payment}
                    direction="vertical"
                    errorText="Please tell us how you'll pay for the consultation."
                  />
                  {detailsForm.payment.value &&
                    (detailsForm.payment.value === 'insurance' ? (
                      <>
                        <div className="mt-20">
                          <FormField
                            field={detailsForm.insurer}
                            errorEmpty="Please select your insurer"
                          />
                        </div>
                        {detailsForm.insurer.value === 'other' && (
                          <div className="mt-20">
                            <FormField
                              field={detailsForm.insurer_name}
                              errorEmpty="Please enter your insurer."
                            />
                          </div>
                        )}
                        <div className="mt-20">
                          <FormField
                            field={detailsForm.policy_number}
                            errorEmpty="Please enter your policy number."
                          />
                        </div>
                        <div className="mt-20">
                          <FormField
                            field={detailsForm.authorization_code}
                            errorText="Please only enter the following: a-z A-Z 0-9 \  / : . - Maximum of 52 characters."
                          />
                        </div>
                        <div className="mt-20">
                          <p>
                            Please note that without an authorisation code you are liable for the
                            full self-pay cost of your initial appointment.
                          </p>
                        </div>
                      </>
                    ) : (
                      <p className="cash-or-credit mt-20">
                        We may contact you in advance of the appointment to take payment, otherwise
                        you&apos;ll receive an invoice after your consultation. Please note that if
                        you do not attend your appointment, you will be liable for the full cost.
                      </p>
                    ))}
                </div>
                <div>
                  <FormField
                    field={detailsForm.has_gp_referral}
                    direction="horizontal"
                    errorText="Please tell us whether you have a GP referral or not."
                  />
                  {detailsForm.has_gp_referral.value &&
                    detailsForm.has_gp_referral.value === 'yes' && (
                      <p className="mt-20">
                        Please bring your referral letter to your appointment. Before your
                        appointment, we&apos;ll contact you to ask for any relevant documents you
                        have, such as pathology and imaging reports.
                      </p>
                    )}
                  {detailsForm.has_gp_referral.value && (
                    <div>
                      <p className="mt-20">
                        If you have any of the following symptoms, please do not continue your
                        online booking and contact your GP or NHS 999/111.
                      </p>
                      <ul className="mt-20">
                        <li>Sudden onset chest pain/heaviness in your chest</li>
                        <li>Heart palpitations with difficulty in breathing</li>
                        <li>Sudden difficulty in breathing</li>
                        <li>Seizure/fitting affecting consciousness and ongoing symptoms</li>
                        <li>
                          Severe allergic reaction with breathing difficulties or swelling of the
                          mouth
                        </li>
                        <li>
                          Sudden onset neurological changes such as weakness, slurring of speech,
                          drowsiness, sudden onset severe headache in the back of the head or neck
                          stiffness with fever
                        </li>
                        <li>Uncontrollable bleeding or a significant injury/accident</li>
                        <li>Pregnancy-related concerns</li>
                        <li>
                          Feeling suicidal – If you are considering harming yourself please contact
                          NHS111 or the Samaritans 116 123
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
                <div>
                  <FormField
                    field={detailsForm.send_discharge_details_to_gp}
                    direction="horizontal"
                    errorText="Please tell us whether you wish to have your discharge details to go to your GP."
                  />
                  {detailsForm.send_discharge_details_to_gp.value === 'yes' && (
                    <div className="mt-40">
                      <FormField field={detailsForm.gp_details} />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="communication__wrapper mb-40">
              <div className="mb-40">
                <FormField field={detailsForm.communication_preferences} />
              </div>
              <p className="mb-30">
                On occasion Nuffield Health may contact you with pertinent service information in
                regards to services we provide. Note that you can either amend or withdraw your
                consent at any time.
              </p>
              <p>
                For information about where your personal data may be processed, how it may be
                processed and for details of our Data Protection Officer, please see our Privacy
                Policy.
              </p>
            </div>
            {((!mandatoryPayment && !optionalPayment) || bypassPayment) && (
              <div className="custom-checkbox__wrapper">
                <input
                  className="custom-checkbox__value"
                  id="toc"
                  type="checkbox"
                  onChange={toggleTerms}
                />
                <label className="custom-checkbox__label" htmlFor="toc">
                  <span className="custom-checkbox__faux">
                    <Checkmark />
                  </span>
                  <span>I agree to the </span>
                  <a
                    target="_blank"
                    rel="noreferrer noopener"
                    href="https://www.nuffieldhealth.com/terms/nuffield-health-website-terms-and-conditions"
                  >
                    terms and conditions
                  </a>
                  <span> and </span>
                  <a
                    target="_blank"
                    rel="noreferrer noopener"
                    href="https://www.nuffieldhealth.com/privacy"
                  >
                    privacy policy
                  </a>
                </label>
              </div>
            )}

            <div className="ctas__wrapper mt-40">
              {(!mandatoryPayment && !optionalPayment) || bypassPayment ? (
                <>
                  <CTAButton
                    action={action}
                    onAction={result => {
                      if (result.statusCode === 'true') {
                        history.replace(`/consultants/${state.consultant.gmcCode}/booked/`, {
                          ...state,
                          gpReferral: detailsForm.has_gp_referral.value === 'yes',
                          consultationFor: patientMakingTheirOwnBooking ? 'myself' : 'someone else',
                          paymentMethod: detailsForm.payment.value,
                          mandatoryPayment: false,
                          optionalPayment: false,
                          paymentToken: false,
                          formValues: {
                            payment: detailsForm.payment.value,
                            insurer: detailsForm.insurer.value,
                          },
                        });
                      } else {
                        logError({
                          error: 'CUSTOM',
                          text: 'Sorry but we are unable to process your booking online. Please call 0345 602 9262 to complete your booking.',
                        });
                      }
                    }}
                  />
                  <CTAButton
                    className="back-btn"
                    action={{
                      call: async () => {
                        scrollParent();
                        history.goBack();
                      },
                      name: 'Back',
                      type: 'BACK',
                    }}
                  />
                </>
              ) : (
                <>
                  <CTAButton
                    className="back-btn"
                    action={{
                      call: async () => {
                        scrollParent();
                        history.goBack();
                      },
                      name: 'Back',
                      type: 'BACK',
                    }}
                  />
                  <CTAButton
                    action={{
                      name: 'Continue to summary',
                      call: formComplete
                        ? async () => {
                            history.replace(`/consultants/${state.consultant.gmcCode}/summary/`, {
                              ...state,
                              mandatoryPayment: mandatoryPayment,
                              optionalPayment: optionalPayment,
                              consultationFor: patientMakingTheirOwnBooking
                                ? 'myself'
                                : 'someone else',
                              formValues: {
                                title: detailsForm.title.value,
                                first_name: detailsForm.first_name.value,
                                last_name: detailsForm.last_name.value,
                                email: detailsForm.email.value,
                                addresslookup: detailsForm.addresslookup.value,
                                gender: detailsForm.gender.value,
                                date_of_birth: detailsForm.date_of_birth.value,
                                phone_number: detailsForm.phone_number.value,
                                has_gp_referral: detailsForm.has_gp_referral.value,
                                send_discharge_details_to_gp:
                                  detailsForm.send_discharge_details_to_gp.value,
                                gp_details: detailsForm.gp_details.value,
                                payment: detailsForm.payment.value,
                                insurer: detailsForm.insurer.value,
                                policy_number: detailsForm.policy_number.value,
                                authorization_code: detailsForm.authorization_code.value,
                                patientMakingTheirOwnBooking: patientMakingTheirOwnBooking,
                              },
                            });

                            scrollParent();
                          }
                        : null,
                      type: 'SUMMARY',
                    }}
                  />
                </>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
};
